import type { Notifier } from "../NotificationScheduler.js";

/**
 * Tauri adapter: emits one OS notification now (the scheduler owns timing).
 * Tauri 2's notification plugin only exposes title/body portably — no data-URL
 * icon or accent color cross-platform — so `color` is dropped here for now.
 */
type TauriNotification = {
  sendNotification: (o: { title: string; body: string }) => Promise<void>;
  isPermissionGranted: () => Promise<boolean>;
  requestPermission: () => Promise<string>;
};

async function tauriNotification(): Promise<TauriNotification> {
  const spec = "@tauri-apps/plugin-notification";
  return (await import(/* @vite-ignore */ spec)) as TauriNotification;
}

export const tauriNotifier: Notifier = {
  async send({ title, body }) {
    const mod = await tauriNotification();
    if (!(await mod.isPermissionGranted())) await mod.requestPermission();
    await mod.sendNotification({ title, body });
  },

  async sendTest({ atMs }) {
    const mod = await tauriNotification();
    if (!(await mod.isPermissionGranted())) {
      const p = await mod.requestPermission();
      if (p !== "granted") throw new Error("Notification permission was denied.");
    }
    const fire = () => void mod.sendNotification({
      title: "Test notification",
      body: atMs ? "Scheduled test fired." : "Immediate test — notifications are working.",
    });
    // Tauri's plugin sends immediately; a delayed test relies on the app staying open.
    if (atMs) setTimeout(fire, Math.max(0, atMs - Date.now()));
    else fire();
    return { background: false };
  },
};
